<template>
  <ReviewStatusCompact
    v-if="compact"
    :has-any-up-to-date-review="hasAnyUpToDateReview"
    :has-up-to-date-review-by-user="hasUpToDateReviewByUser"
    :review-loading="reviewLoading"
    :reviewed="reviewed"
    :reviews-to-show="reviewsToShow"
    :tracking-prefix="trackingPrefix"
    @toggle-reviewed="toggleReviewed"
  />

  <div
    v-else-if="reviewed"
    v-tooltip.html.placementBottom.textLeft="aggregateTooltip"
    class="
      tw-rounded-r-lg
      tw-group
      tw-pl-5
      tw-pr-2
      tw-flex
      tw-items-center
      tw-relative
      tw-z-1
    "
    data-testid="reconciliation-review-status"
    :class="containerClass"
  >
    <component
      :is="iconComponent"
      v-if="hasAnyUpToDateReview"
      class="tw-mr-2 workflow-status-icon"
    />

    <WorkflowItemStatusReviewedBy
      class="tw-flex-1 tw-min-w-0"
      :has-up-to-date-review-by-user="hasUpToDateReviewByUser"
      :has-any-up-to-date-review="hasAnyUpToDateReview"
      :reviews-to-show="reviewsToShow"
    />

    <UnmarkButton
      v-if="hasUpToDateReviewByUser"
      :tooltip="I18n.t('reconciliation_review.unmark')"
      :background="'tw-bg-[linear-gradient(90deg,_rgba(236,253,245,0)_0%,_rgba(236,253,245,1)_60%,_rgba(236,253,245,1)_100%)]'"
      :tracking-prefix="trackingPrefix"
      :tracking-action="'unmark-as-reviewed'"
      :disabled="reviewLoading"
      @on-mouse-enter="onUnmarkButtonMouseEnter"
      @on-mouse-leave="onUnmarkButtonMouseLeave"
      @unmark="toggleReviewed(false)"
    />

    <SfButtonInverted
      v-else
      :data-heap="`${trackingPrefix}-review-status-mark-as-reviewed`"
      :disabled="reviewLoading"
      class="tw-rounded-md tw-ml-1 tw-flex-shrink-0 !tw-text-inherit"
      variant="secondary"
      small
      @click.stop.prevent="toggleReviewed(true)"
    >
      {{ I18n.t('reconciliation_review.mark_as_reviewed') }}
    </SfButtonInverted>
  </div>

  <SfButtonInverted
    v-else
    class="
      tw-flex tw-items-center tw-rounded-none tw-rounded-r-lg
      !tw-border-none
      tw-pl-6
      !tw-text-inherit
    "
    data-testid="reconciliation-review-status"
    :data-heap="`${trackingPrefix}-review-status-mark-as-reviewed`"
    :disabled="reviewLoading"
    variant="secondary"
    @click.stop.prevent="toggleReviewed(true)"
  >
    {{ I18n.t('reconciliation_review.mark_as_reviewed') }}
  </SfButtonInverted>
</template>
<script lang="ts" setup>
import useI18n from '@app/shared/vue/composables/use-I18n'
import WorkflowItemStatusReviewedBy from '@app/application/workflows/screens/workflow-review/components/workflow-item-status/reviewed-by.vue'
import { computed, ref } from 'vue'
import { formatReviewTooltip } from '@app/application/workflows/utils/format-preparation-and-review'
import UnmarkButton from './unmark-button.vue'
import { getReviewCounterIcon } from '@app/shared/assets/icons/review-counter-icons'
import SfButtonInverted from '@app/shared/vue/components/sf-button/sf-button-inverted.vue'
import ReviewStatusCompact from './review-status-compact.vue'
import type { TemplateReview } from '@app/application/workflows/types'

const I18n = useI18n()

interface Props {
  hasAnyUpToDateReview: boolean
  hasUpToDateReviewByUser: boolean
  reviewLoading: boolean
  reviewed: boolean
  reviewsToShow: Array<TemplateReview>
  compact: boolean
  trackingPrefix: string
}

const props = defineProps<Props>()

const emit = defineEmits<{ 'toggle-reviewed': [boolean] }>()

const containerClass = computed(() => {
  if (props.hasUpToDateReviewByUser) {
    return 'tw-bg-emerald-50 tw-text-emerald-600'
  }
  if (props.hasAnyUpToDateReview) {
    return 'tw-bg-neutral-100'
  }

  return 'tw-bg-yellow-50'
})

const hasTooltip = ref(true)

const iconComponent = computed(() => {
  if (!props.hasAnyUpToDateReview) {
    return ''
  }

  return getReviewCounterIcon(
    props.hasUpToDateReviewByUser,
    props.reviewsToShow.length
  )
})

const aggregateTooltip = computed(() => {
  if (!hasTooltip.value) {
    return ''
  }

  return formatReviewTooltip(props.reviewsToShow, props.hasAnyUpToDateReview)
})

function toggleReviewed(reviewed: boolean) {
  emit('toggle-reviewed', reviewed)
}
function onUnmarkButtonMouseEnter() {
  hasTooltip.value = false
}
function onUnmarkButtonMouseLeave() {
  hasTooltip.value = true
}
</script>
